/**
 * src/state/teardownBackup.js
 *
 * localStorage backstop for a teardown (beforeunload/pagehide) state write that
 * has no reliable delivery mechanism once the page starts unloading — see the
 * long rationale at usePersistence.js's teardown handler. This module owns the
 * key shape and nothing else; usePersistence writes and replays, useAuth clears
 * on logout.
 *
 * GH #6307 — the key is scoped to the TENANT AND THE USER.
 *
 * The original key was tenant-only, which made the backup replayable by whoever
 * logged into that tenant next on the same browser: user A's unconfirmed
 * whole-state edit landed under user B's token, attributed to B in the activity
 * trail, announced by nothing but a console warning. F-37 had already decided
 * this exact question for the smaller artefact (in-flight document drafts are
 * cleared on logout on a shared-device rationale); a whole-tenant-state blob is
 * strictly more than a draft.
 *
 * Two controls, because either alone leaves a hole:
 *   1. The key carries the user id, so no other principal can even find the
 *      backup — including a session that never logs out (a shared browser left
 *      open, a session that simply expires).
 *   2. logout() clears the logged-out user's own backup, so a deliberate logout
 *      does not leave a whole-state blob behind on a shared device at all.
 *
 * Backups written under the old tenant-only key are INVALIDATED, never
 * migrated: there is no way to tell from the stored value which user took it,
 * and guessing is the exposure. purgeLegacyTeardownBackups() removes them on
 * the first load after this ships.
 */

const TEARDOWN_BACKUP_KEY_PREFIX = 'seasuite:teardown-backup:';

// The user-scoped key is `<prefix><tenantId>::<userId>`. '::' rather than a
// single colon so a tenant id that ever contained a colon could not make a
// legacy key look user-scoped — the legacy sweep below keys off exactly this.
const OWNER_SEPARATOR = '::';

/**
 * GH #6307 — a backup is a recovery path for a save that failed moments ago,
 * not an archive. Without a bound, a dormant tenant could replay an edit months
 * after it was taken, under a version guard that says only "nobody else has
 * written since" — which on a quiet tenant is exactly the case that stays true.
 * 24h comfortably covers "the browser crashed, I came back tomorrow" and
 * nothing beyond it.
 */
export const TEARDOWN_BACKUP_MAX_AGE_MS = 24 * 60 * 60 * 1000;

function keyFor(tenantId, userId) {
  return TEARDOWN_BACKUP_KEY_PREFIX + tenantId + OWNER_SEPARATOR + userId;
}

/**
 * Store a teardown backup.
 *
 * The stored shape is `{ payload, version, ts }` plus, since GH #6607, two
 * optional classification fields:
 *
 *   changedKeys  the top-level keys the user's edit actually changed relative
 *                to the last acknowledged server state
 *   baseline     that acknowledged server state, restricted to `changedKeys`
 *
 * Those two exist so the replay can resolve a version mismatch the way the
 * 409 handler does — merge the user's records onto whatever the row holds now
 * — instead of guessing. They are OPTIONAL because they are also the only
 * part of the backup that can push it over the storage quota: a caller that
 * fails to store the full shape retries with the plain one rather than
 * storing nothing, so the recovery path this module exists for degrades to
 * its pre-#6607 behaviour instead of disappearing.
 *
 * @returns {boolean} true when the backup was actually stored.
 */
export function writeTeardownBackup(tenantId, userId, backup) {
  if (!tenantId || !userId) return false;
  try {
    window.localStorage.setItem(keyFor(tenantId, userId), JSON.stringify(backup));
    return true;
  } catch {
    /* private-mode / quota-exceeded / storage disabled — no worse off than before this fix */
    return false;
  }
}

export function readTeardownBackup(tenantId, userId) {
  if (!tenantId || !userId) return null;
  try {
    const raw = window.localStorage.getItem(keyFor(tenantId, userId));
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function clearTeardownBackup(tenantId, userId) {
  if (!tenantId || !userId) return;
  try {
    window.localStorage.removeItem(keyFor(tenantId, userId));
  } catch {
    /* ignore */
  }
}

/** A backup older than the bound is not a recovery path any more. */
export function isTeardownBackupStale(backup, now = Date.now()) {
  const ts = Number(backup?.ts);
  if (!Number.isFinite(ts)) return true; // no timestamp — cannot be bounded, so not replayable
  return now - ts > TEARDOWN_BACKUP_MAX_AGE_MS;
}

/**
 * Remove every backup still stored under the pre-#6307 tenant-only key.
 *
 * Deliberately a delete rather than a migration: the stored value records no
 * owner, so re-keying one would be a guess about whose edit it is, and a wrong
 * guess is the cross-user replay this change exists to stop. The cost is a
 * single unconfirmed save, in a window that only opens when a save had already
 * failed; the alternative is carrying the exposure forward.
 *
 * Safe to call on every page, including the session-less portals — it only ever
 * removes keys, and only ones this app wrote.
 */
export function purgeLegacyTeardownBackups() {
  try {
    const storage = window.localStorage;
    const legacy = [];
    for (let i = 0; i < storage.length; i++) {
      const key = storage.key(i);
      if (!key || !key.startsWith(TEARDOWN_BACKUP_KEY_PREFIX)) continue;
      const suffix = key.slice(TEARDOWN_BACKUP_KEY_PREFIX.length);
      if (!suffix.includes(OWNER_SEPARATOR)) legacy.push(key);
    }
    // Collected first, then removed: removing inside the loop reindexes
    // localStorage under the cursor and skips the following key.
    legacy.forEach((key) => storage.removeItem(key));
    return legacy.length;
  } catch {
    return 0;
  }
}
